import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  AssignedCommentItem,
  AssignedCommentsFilterOption,
  AssignedCommentsListResult,
  AssignedCommentsReadFilter,
  AssignedCommentsUnreadCount,
} from '../models/assigned-comments.models';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class AssignedCommentsApiService {
  private readonly http = inject(HttpClient);

  async getAssignedComments(params: {
    page: number;
    pageSize: number;
    readFilter: AssignedCommentsReadFilter;
    workflowId: string | null;
    projectId: string | null;
    search: string;
  }): Promise<AssignedCommentsListResult> {
    let httpParams = new HttpParams()
      .set('page', String(params.page))
      .set('pageSize', String(params.pageSize));

    if (params.readFilter !== 'all') {
      httpParams = httpParams.set('isRead', String(params.readFilter === 'read'));
    }
    if (params.workflowId) {
      httpParams = httpParams.set('workflowId', params.workflowId);
    }
    if (params.projectId) {
      httpParams = httpParams.set('projectId', params.projectId);
    }
    if (params.search.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    const response = await firstValueFrom(
      this.http.get<unknown>('/api/assigned-comments', { params: httpParams })
    );
    return this.mapListResponse(response, params.page, params.pageSize);
  }

  async getUnreadCount(): Promise<AssignedCommentsUnreadCount> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/assigned-comments/unread-count'));
    const source = this.asRecord(response);
    return { count: this.asNumber(source['unreadCount'], this.asNumber(source['count'])) };
  }

  async markAsRead(id: string): Promise<void> {
    await firstValueFrom(this.http.patch(`/api/assigned-comments/${id}/read`, {}));
  }

  async markAsUnread(id: string): Promise<void> {
    await firstValueFrom(this.http.patch(`/api/assigned-comments/${id}/unread`, {}));
  }

  private mapListResponse(
    raw: unknown,
    fallbackPage: number,
    fallbackPageSize: number
  ): AssignedCommentsListResult {
    const source = this.asRecord(raw);
    const itemsRaw = Array.isArray(raw)
      ? raw
      : Array.isArray(source['items'])
        ? source['items']
        : Array.isArray(source['data'])
          ? source['data']
          : [];

    const workflowsRaw = Array.isArray(source['workflows'])
      ? source['workflows']
      : Array.isArray(source['workflowOptions'])
        ? source['workflowOptions']
        : [];
    const projectsRaw = Array.isArray(source['projects'])
      ? source['projects']
      : Array.isArray(source['projectOptions'])
        ? source['projectOptions']
        : [];

    return {
      items: itemsRaw.map((entry) => this.mapItem(entry)).filter((item) => !!item.id),
      page: this.asNumber(source['page'], fallbackPage),
      pageSize: this.asNumber(source['pageSize'], fallbackPageSize),
      totalCount: this.asNumber(source['totalCount'], this.asNumber(source['total'])),
      workflowOptions: workflowsRaw
        .map((item) => this.mapFilterOption(item))
        .filter((item) => !!item.id),
      projectOptions: projectsRaw
        .map((item) => this.mapFilterOption(item))
        .filter((item) => !!item.id),
    };
  }

  private mapItem(raw: unknown): AssignedCommentItem {
    const source = this.asRecord(raw);
    const commentId = this.asString(source['commentId']) || this.asString(source['id']);
    const metadata = this.asRecord(source['metadata']);

    return {
      id: this.asString(source['id']) || commentId,
      commentId,
      cardId:
        this.asString(source['cardId']) ||
        this.asString(metadata['cardId']) ||
        this.asString(source['sourceEntityId']),
      cardTitle:
        this.asString(source['cardTitle']) ||
        this.asString(metadata['cardTitle']) ||
        this.asString(source['title']) ||
        'Card',
      commentPreview:
        this.asString(source['commentTextPreview']) ||
        this.asString(source['commentPreview']) ||
        this.asString(source['body']) ||
        this.asString(source['description']),
      authorName:
        this.asString(source['authorName']) ||
        this.asString(metadata['authorName']) ||
        this.asString(source['author']) ||
        '-',
      projectId: this.asNullableString(source['projectId']) || this.asNullableString(metadata['projectId']),
      projectName:
        this.asString(source['projectName']) || this.asString(metadata['projectName']) || '-',
      workflowId:
        this.asNullableString(source['workflowId']) || this.asNullableString(metadata['workflowId']),
      workflowName:
        this.asString(source['workflowName']) || this.asString(metadata['workflowName']) || '-',
      occurredAt: this.asString(source['occurredAt']) || this.asString(source['createdAt']),
      isRead: this.asBoolean(source['isRead']),
    };
  }

  private mapFilterOption(raw: unknown): AssignedCommentsFilterOption {
    const source = this.asRecord(raw);
    return {
      id: this.asString(source['id']) || this.asString(source['value']),
      label: this.asString(source['label']) || this.asString(source['name']),
    };
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    const parsed = this.asString(value).trim();
    return parsed ? parsed : null;
  }

  private asBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }

  private asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' ? value : fallback;
  }
}
